import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError, type U3142InitLU } from "./U3142Service";
import { U3142ContextProvider } from "./U3142Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3142";

export const U3142ROLES = {
  READ: "U3142:READ",
  ADD: "U3142:ADD",
  EDIT: "U3142:EDIT",
  DELETE: "U3142:DELETE",
  AUDITS: "U3142:AUDITS",
  EXPORT: "U3142:EXPORT",
  /** Préréglages barre d’outils (éditeur PDF annotations) : créer / modifier / supprimer des modèles. */
  EDITPRESET: "U3142:EDITPRESET",
} as const;
export type U3142ROLES = (typeof U3142ROLES)[keyof typeof U3142ROLES];

const U3142: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3142InitLU>({
    launchSearch: false,
    hideSearch: true,
    nbRecordPerPage: 50,
    lotDetailOperationTypes: [],
  });
  // charge fichiers de traduction
  useEffect(() => {
    i18next.loadNamespaces(["u3142", "u3140"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  // Gestion de l'initialisation de l'unité logique
  const handleInitLU = useU2000InitLUHandlers<U3142InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3142InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  // Initialisation de l'unité logique au montage du composant et apres quelques changements de langue
  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  // Set LU title and breadcrumb
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3142ContextProvider initLU={initLUData}>
      <Outlet />
    </U3142ContextProvider>
  );
};

export default U3142;
